import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useReportTemplates } from '@/hooks/useReportTemplates';
import { useReportPreferences } from '@/hooks/useReportPreferences';
import { ReportDisplayOptionsPanel } from '@/components/reports/ReportDisplayOptionsPanel';
import type { ReportDisplayOptions } from '@/components/reports/ReportDisplayOptionsPanel';
import {
  startBatchRun,
  createBatchDefinition,
  type BatchDefinitionItemSchema,
  type BatchDefinitionResponse,
} from '@/api/batchReportsApi';
import { useToast } from '@/components/ui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { projectApi } from '@/api/projectApi';
import { API_BASE_URL } from '@/config';

const DEFAULT_DISPLAY_OPTIONS: ReportDisplayOptions = {
  sections: {
    data_date: { visible: true },
    generated_date: { visible: true },
    project_name: { visible: true },
    summary_heading: { visible: true },
    calculation_and_logic: { visible: false },
    client_logo: { visible: false, scale_percent: 100 },
    footer_confidential: { visible: true },
    footer_page_numbers: { visible: true },
  },
  header: { show_time: false },
};

export interface BatchBuilderProps {
  /** Pre-populate from a saved batch definition (e.g. when user clicked "Run" on a saved batch) */
  initialBatchDefinition?: BatchDefinitionResponse | null;
  className?: string;
}

export const BatchBuilder: React.FC<BatchBuilderProps> = ({
  initialBatchDefinition = null,
  className = '',
}) => {
  const { selectedProject } = useGlobalProject();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { data: templates = [] } = useReportTemplates();
  const { getPreference } = useReportPreferences();

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(() => {
    if (initialBatchDefinition?.items?.length) {
      return new Set(initialBatchDefinition.items.map((i) => i.template_id));
    }
    return new Set();
  });
  const [optionsByTemplate, setOptionsByTemplate] = useState<
    Record<string, ReportDisplayOptions>
  >(() => {
    const out: Record<string, ReportDisplayOptions> = {};
    if (initialBatchDefinition?.items?.length) {
      initialBatchDefinition.items.forEach((i) => {
        out[i.template_id] = (i.display_options as ReportDisplayOptions) ?? DEFAULT_DISPLAY_OPTIONS;
      });
    }
    return out;
  });
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [openPanels, setOpenPanels] = useState<Set<string>>(new Set());
  const [hasClientLogo, setHasClientLogo] = useState(false);
  const [baselineSnapshot, setBaselineSnapshot] = useState<{ id: string } | null>(null);

  // Fetch project logo for Client Logo option in display settings
  useEffect(() => {
    if (!selectedProject?.id) {
      setHasClientLogo(false);
      return;
    }
    let cancelled = false;
    projectApi.getProjectLogo(String(selectedProject.id)).then((result) => {
      if (cancelled) return;
      setHasClientLogo(!!result);
    });
    return () => { cancelled = true; };
  }, [selectedProject?.id]);

  // Fetch baseline for current project (reports with needs_baseline require it)
  useEffect(() => {
    if (!selectedProject?.id) {
      setBaselineSnapshot(null);
      return;
    }
    const projectId = String(selectedProject.id);
    fetch(`${API_BASE_URL}/v1/snapshots/${projectId}/baseline`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { id?: string } | null) => {
        setBaselineSnapshot(data?.id ? { id: String(data.id) } : null);
      })
      .catch(() => setBaselineSnapshot(null));
  }, [selectedProject?.id]);

  const getOptionsForTemplate = useCallback(
    (templateId: string): ReportDisplayOptions => {
      if (optionsByTemplate[templateId]) return optionsByTemplate[templateId];
      const pref = getPreference(templateId);
      if (pref) return pref;
      return DEFAULT_DISPLAY_OPTIONS;
    },
    [optionsByTemplate, getPreference]
  );

  const setOptionsForTemplate = useCallback((templateId: string, options: ReportDisplayOptions) => {
    setOptionsByTemplate((prev) => ({ ...prev, [templateId]: options }));
  }, []);

  const toggleTemplate = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (template?.needs_baseline && !baselineSnapshot) return;
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  };

  const togglePanel = (templateId: string) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  };

  const handleSaveBatch = async () => {
    const name = saveName.trim();
    if (!name) {
      showToast('error', 'Enter a name');
      return;
    }
    if (selectedTemplateIds.size === 0) {
      showToast('error', 'Select at least one template');
      return;
    }
    setSaving(true);
    try {
      const items: BatchDefinitionItemSchema[] = Array.from(selectedTemplateIds).map(
        (templateId, idx) => ({
          template_id: templateId,
          display_options: getOptionsForTemplate(templateId),
          sort_order: idx,
        })
      );
      await createBatchDefinition({ name, items });
      queryClient.invalidateQueries({ queryKey: ['batchDefinitions'] });
      showToast('success', 'Batch saved');
      setSaveName('');
    } catch (e) {
      showToast('error', (e as Error).message || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  const handleRunBatch = async () => {
    if (!selectedProject?.id) {
      showToast('error', 'Select a project first');
      return;
    }
    if (selectedTemplateIds.size === 0) {
      showToast('error', 'Select at least one template');
      return;
    }
    const selectedTemplatesWithBaseline = selectedList.map((id) => templates.find((t) => t.id === id)).filter(Boolean);
    const needsBaselineMissing = selectedTemplatesWithBaseline.some(
      (t) => t?.needs_baseline && !baselineSnapshot
    );
    if (needsBaselineMissing) {
      showToast('error', 'A baseline must be set for this project to run some of the selected reports. Set one in Baselines.');
      return;
    }
    setRunning(true);
    try {
      const items = Array.from(selectedTemplateIds).map((templateId) => ({
        template_id: templateId,
        display_options: getOptionsForTemplate(templateId),
      }));
      const run = await startBatchRun({
        project_id: String(selectedProject.id),
        items,
      });
      showToast('success', 'Batch run started');
      navigate(`/app/reports/batch/${run.id}`);
    } catch (e) {
      showToast('error', (e as Error).message || 'Failed to start batch');
    } finally {
      setRunning(false);
    }
  };

  const selectedList = Array.from(selectedTemplateIds);
  const projectId = selectedProject?.id ? String(selectedProject.id) : null;

  return (
    <div className={className}>
      <PageHeader
        title="Batch Reports"
        subtitle="Select templates, set display options, then run or save a batch for the current project."
        titleClassName="font-medium"
      />

      {/* Project context and primary action: run batch for this project */}
      <div className="mt-4 p-4 border border-border rounded-lg bg-card flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-foreground">
          <span className="text-muted-foreground">Current project: </span>
          {selectedProject?.name ?? 'Select a project'}
        </div>
        <Button
          onClick={handleRunBatch}
          disabled={!projectId || selectedTemplateIds.size === 0 || running}
        >
          {running ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Starting…
            </>
          ) : (
            'Run batch'
          )}
        </Button>
      </div>

      {/* Build your batch: templates and options */}
      <div className="mt-8 space-y-8">
        <section>
          <h2 className="text-sm font-medium text-foreground mb-3">Templates</h2>
          {!baselineSnapshot && templates.some((t) => t.needs_baseline) && (
            <p className="text-xs text-muted-foreground mb-2">
              Set a baseline in Baselines to enable reports that require it.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 border border-border rounded-lg p-4 bg-card">
            {templates.map((t) => {
              const needsBaselineDisabled = t.needs_baseline && !baselineSnapshot;
              return (
                <label
                  key={t.id}
                  className={`flex items-start gap-3 text-sm text-foreground self-start py-1 ${needsBaselineDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  <Checkbox
                    checked={selectedTemplateIds.has(t.id)}
                    onCheckedChange={() => toggleTemplate(t.id)}
                    disabled={needsBaselineDisabled}
                  />
                  <span className="truncate">
                    {t.name}
                    {needsBaselineDisabled && (
                      <span className="block text-xs text-muted-foreground mt-0.5">Requires baseline</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </section>

        {selectedList.length > 0 && (
          <section>
            <h2 className="text-sm font-medium text-foreground mb-3">Display options per template</h2>
            <div className="space-y-2">
              {selectedList.map((templateId) => {
                const template = templates.find((t) => t.id === templateId);
                const name = template?.name ?? templateId;
                const open = openPanels.has(templateId);
                return (
                  <Collapsible
                    key={templateId}
                    open={open}
                    onOpenChange={() => togglePanel(templateId)}
                  >
                    <div className="border border-border rounded-lg bg-card">
                      <CollapsibleTrigger
                        type="button"
                        className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg"
                      >
                        {open ? (
                          <ChevronDown className="h-4 w-4 shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0" />
                        )}
                        {name}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-2 border-t border-border">
                          <ReportDisplayOptionsPanel
                            templateId={templateId}
                            displayOptions={getOptionsForTemplate(templateId)}
                            onDisplayOptionsChange={(opts) => setOptionsForTemplate(templateId, opts)}
                            hasClientLogo={hasClientLogo}
                          />
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-sm font-medium text-foreground mb-3">Save as batch</h2>
          <p className="text-xs text-muted-foreground mb-3">Save this selection and options so you can re-run it later from Saved Batches.</p>
          <div className="flex flex-wrap items-center gap-4">
            <Input
              placeholder="Batch name (e.g. Monthly Health Pack)"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="max-w-xs"
            />
            <Button
              variant="outline"
              onClick={handleSaveBatch}
              disabled={!saveName.trim() || selectedTemplateIds.size === 0 || saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
